import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, Overline, CTAButton, ArrowLink } from "@/components/primitives";
import { getArticle, ARTICLES } from "@/data/insights";
import Seo from "@/components/Seo";
import { organizationLd, websiteLd, breadcrumbLd, faqLd, articleLd } from "@/lib/seo";
import { Calculator } from "lucide-react";

export default function InsightArticle() {
  const { slug } = useParams();
  const article = getArticle(slug);
  if (!article) return <Navigate to="/insights" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 3);

  const jsonLd = [
    organizationLd(),
    websiteLd(),
    articleLd(article, slug),
    breadcrumbLd(`/insights/${slug}`),
    article.faqs ? faqLd(article.faqs) : null,
  ];

  return (
    <>
      <Seo
        title={`${article.title} | Bharat Ethanol Forum`}
        description={article.dek}
        path={`/insights/${slug}`}
        image={article.image}
        jsonLd={jsonLd}
      />
      <section className="relative overflow-hidden bg-navy grain pt-36 pb-16 md:pt-44 md:pb-20">
        <img src={article.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="relative mx-auto max-w-3xl px-6">
          <Reveal>
            <nav className="mb-5 text-sm text-slate-400">
              <Link to="/" className="hover:text-white">Home</Link> <span className="text-slate-600">›</span>{" "}
              <Link to="/insights" className="hover:text-white">Insights</Link> <span className="text-slate-600">›</span>{" "}
              <span className="text-emerald-300">Article</span>
            </nav>
            <h1 className="font-display text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl lg:text-5xl">{article.title}</h1>
            <p className="mt-4 text-sm text-slate-400">Last updated {article.updated} · {article.readTime}</p>
          </Reveal>
        </div>
      </section>

      <article className="bg-parchment py-16 md:py-20">
        <div className="mx-auto max-w-3xl px-6">
          {/* Definition box */}
          <Reveal>
            <div className="rounded-2xl border-l-4 border-emerald2 bg-white p-6 shadow-sm">
              <p className="text-lg leading-relaxed text-navy/80">{article.definition}</p>
            </div>
          </Reveal>

          {/* Sections */}
          <div className="mt-10 space-y-10">
            {article.sections.map((s) => (
              <Reveal key={s.h}>
                <h2 className="font-display text-2xl font-bold tracking-tight text-navy">{s.h}</h2>
                <p className="mt-4 text-lg leading-relaxed text-navy/70">{s.p}</p>
              </Reveal>
            ))}
          </div>

          {/* Table */}
          {article.table && (
            <Reveal>
              <div className="mt-12 overflow-hidden rounded-2xl border border-border bg-white shadow-sm">
                <p className="border-b border-border bg-navy px-6 py-4 font-display font-bold text-white">{article.table.title}</p>
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-border bg-parchment">
                      {article.table.head.map((h) => <th key={h} className="px-6 py-3 font-semibold text-navy">{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {article.table.rows.map((r, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        {r.map((c, j) => <td key={j} className="px-6 py-3 text-navy/70">{c}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Reveal>
          )}

          {/* Calculator CTA */}
          <Reveal>
            <div className="mt-12 flex flex-col items-start gap-4 rounded-2xl border border-emerald2/30 bg-white p-6 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald2/10 text-emeraldDeep"><Calculator className="h-6 w-6" /></span>
                <p className="font-semibold text-navy">See the numbers for yourself.</p>
              </div>
              <CTAButton to="/ethanol-impact-calculator" variant="emerald" className="shrink-0">Run the Calculator</CTAButton>
            </div>
          </Reveal>

          {/* FAQ */}
          {article.faqs && (
            <Reveal>
              <div className="mt-14">
                <Overline className="mb-5">Frequently asked</Overline>
                <Accordion type="single" collapsible className="rounded-2xl border border-border bg-white px-2">
                  {article.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`faq-${i}`} className="border-border">
                      <AccordionTrigger className="px-4 text-left font-display font-bold text-navy hover:no-underline">{f.q}</AccordionTrigger>
                      <AccordionContent className="px-4 text-navy/70">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </Reveal>
          )}

          {!article.pillar && (
            <Reveal>
              <p className="mt-12 text-navy/70">
                Part of our pillar guide — <ArrowLink to="/insights/ethanol-economy-india-2030">India's ethanol economy in 2030</ArrowLink>
              </p>
            </Reveal>
          )}
        </div>
      </article>

      {/* Related */}
      <section className="border-t border-border bg-white py-16">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Overline className="mb-6">Keep reading</Overline>
          <div className="grid gap-6 md:grid-cols-3">
            {related.map((a) => (
              <Link key={a.slug} to={`/insights/${a.slug}`} className="group rounded-2xl border border-border bg-parchment p-6 transition-all hover:-translate-y-1 hover:shadow-lg">
                <h3 className="font-display text-lg font-bold text-navy">{a.title}</h3>
                <p className="mt-2 text-sm text-navy/60">{a.dek}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
